import { useState, useRef } from 'react';
import {
  useShadowRoot,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import { attachTechDocsAddonComponentData } from '@backstage/plugin-techdocs-react/alpha';
import {
  Button,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import CodeIcon from '@material-ui/icons/Code';

/**
 * TechDocs Subheader addon: dropdown "Export" con tres formatos:
 *   - PDF       (html2pdf.js — captura visual del shadow DOM)
 *   - DOCX      (html-docx-js — convierte el HTML a Word)
 *   - Markdown  (raw .md original embebido por el hook embed-source-md.py)
 *
 * Vive fuera del iframe sandboxed → puede usar window.print()-equivalentes,
 * descargas blob, etc., sin bloqueos de Backstage.
 */

const useStyles = makeStyles({
  button: {
    background: '#1D1F50',
    color: '#fff',
    fontSize: '0.72rem',
    fontWeight: 500,
    padding: '4px 10px',
    textTransform: 'none',
    boxShadow: '0 1px 2px rgba(29, 31, 80, 0.15)',
    minWidth: 'auto',
    '&:hover': {
      background: '#11132F',
      boxShadow: '0 2px 6px rgba(29, 31, 80, 0.25)',
    },
    '&.Mui-disabled': {
      background: '#1D1F50',
      color: '#fff',
      opacity: 0.7,
    },
  },
  menuItem: {
    fontSize: '0.85rem',
  },
  menuIcon: {
    minWidth: 32,
    color: '#1D1F50',
  },
});

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const slugify = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .substring(0, 80);

const setVisibility = (el: HTMLElement | null, visible: boolean) => {
  if (!el) return;
  if (visible) {
    el.dataset.aunoaOrigDisplay = el.style.display || '';
    el.style.display = 'block';
  } else {
    el.style.display = el.dataset.aunoaOrigDisplay ?? '';
    delete el.dataset.aunoaOrigDisplay;
  }
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const findArticle = (root: ShadowRoot | Document) =>
  root.querySelector<HTMLElement>('.md-content__inner') ||
  root.querySelector<HTMLElement>('article.md-typeset') ||
  root.querySelector<HTMLElement>('article') ||
  root.querySelector<HTMLElement>('.md-content');

const computeFilename = (root: ShadowRoot, ext: string): string => {
  const h1 = root.querySelector('h1');
  const title =
    (h1?.textContent || document.title || 'aunoa-doc').trim() || 'aunoa-doc';
  return `${slugify(title) || 'aunoa-doc'}.${ext}`;
};

const readEmbedded = (root: ShadowRoot, id: string): unknown | null => {
  const el = root.getElementById(id);
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────────────────────

async function exportPdf(shadowRoot: ShadowRoot): Promise<void> {
  const article = findArticle(shadowRoot);
  if (!article) throw new Error('No se encuentra el contenido del documento.');

  const cover = shadowRoot.querySelector<HTMLElement>('.print-cover');
  const footer = shadowRoot.querySelector<HTMLElement>('.print-footer');
  setVisibility(cover, true);
  setVisibility(footer, true);

  try {
    const html2pdf = (await import('html2pdf.js')).default;

    // onclone se ejecuta sobre el DOM clonado antes de la captura.
    // Hace 5 cosas:
    //  1. Inyecta <style> con los estilos de impresión como CSS regular
    //     (no @media print, que html2canvas no respeta).
    //  2. Sustituye .print-cover__row (flex) por <table> 2 columnas — flex es
    //     poco fiable en html2canvas, table layout siempre funciona.
    //  3. Limpia mermaid: deja solo el SVG, descarta texto fuente residual.
    //  4. Aplana tabs (pymdownx.tabbed): convierte cada label en heading y
     //     muestra el contenido de TODAS las pestañas.
    //  5. Abre todos los <details> para no perder contenido colapsado.
    const onclone = (clonedDoc: Document) => {
      // 1) Inyectar estilos comprehensivos como CSS normal
      const styleEl = clonedDoc.createElement('style');
      const css = [
        // Ocultar UI mkdocs/Material
        '.md-header, .md-tabs, .md-sidebar, .md-footer, .md-search, .md-source,',
        '.md-skip, .md-content__button, label#toggle-sidebar, .headerlink,',
        'nav, [data-md-component="sidebar"], [data-md-component="navigation"],',
        '[data-md-component="toc"], [data-md-component="footer"],',
        '[data-md-component="header"], [data-md-component="search"],',
        'script { display: none !important; }',
        'body, .md-content, .md-content__inner, article {',
        '  background: #fff !important; color: #1a1a1a !important;',
        '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;',
        '  font-size: 10.5pt !important; line-height: 1.45 !important;',
        '  margin: 0 !important; padding: 0 !important; max-width: 100% !important;',
        '}',
        // Cabecera y pie corporativos
        '.print-cover { display: block !important; margin: 0 0 14pt 0 !important; }',
        '.print-footer { display: block !important; margin-top: 24pt; padding-top: 6pt;',
        '  border-top: 0.5pt solid #E2E8F0; font-size: 8pt; color: #6A7D94; line-height: 1.35; }',
        '.print-footer__left { font-weight: 600; color: #1D1F50; margin-bottom: 2pt; }',
        '.print-footer__center { font-style: italic; }',
        '.print-cover__props-wrap { display: block; margin: 4pt 0 0 0; }',
        '.print-cover__props { width: 100%; border-collapse: collapse; font-size: 8.5pt;',
        '  background: #F5F6FA; border: 1pt solid #E2E8F0; }',
        '.print-cover__props th { text-align: left; color: #1D1F50; font-weight: 600;',
        '  padding: 3pt 8pt; border: none; background: transparent; white-space: nowrap;',
        '  vertical-align: top; width: 1%; }',
        '.print-cover__props td { color: #2a3138; padding: 3pt 10pt 3pt 0; border: none;',
        '  background: transparent; vertical-align: top; }',
        '.print-cover__props tr + tr th, .print-cover__props tr + tr td { border-top: 1px solid #E2E8F0; }',
        '.print-cover__crumbs { font-size: 7.5pt; color: #6A7D94; text-transform: uppercase;',
        '  letter-spacing: 0.08em; margin: 0 0 3pt 0; line-height: 1.2; }',
        '.print-cover__title { font-size: 14pt; font-weight: 700; color: #1D1F50;',
        '  margin: 0 !important; padding: 0 !important; border: none !important; line-height: 1.15; }',
        // Headings
        'h1 { color: #1D1F50; font-size: 16pt; font-weight: 700; margin: 0 0 12pt;',
        '  border-bottom: 0.5pt solid #81B1FA; padding-bottom: 4pt; }',
        'article > h1:first-child, .md-content__inner > h1:first-child { display: none; }',
        'h2 { color: #1D1F50; font-size: 13pt; font-weight: 700; margin: 14pt 0 6pt;',
        '  border-left: 2.5pt solid #81B1FA; padding-left: 7pt; }',
        'h3 { color: #2D3082; font-size: 11pt; font-weight: 600; margin: 10pt 0 4pt; }',
        'h4 { color: #2D3082; font-size: 10pt; font-weight: 600; margin: 8pt 0 3pt; }',
        // Enlaces
        'a { color: #1D1F50; text-decoration: underline; text-decoration-color: #81B1FA; }',
        // Tablas
        'table:not(.aunoa-cover-layout):not(.print-cover__props) { border-collapse: collapse;',
        '  width: 100% !important; font-size: 8.5pt; margin: 8pt 0; border: 0.5pt solid #E2E8F0; }',
        'thead { display: table-header-group; }',
        'thead th { background: #1D1F50 !important; color: #fff !important;',
        '  border: 0.5pt solid #1D1F50 !important; padding: 3pt 5pt !important;',
        '  font-weight: 600; text-align: left; }',
        'tbody tr:nth-child(even) { background: #F5F6FA !important; }',
        'tbody td { border: 0.5pt solid #E2E8F0 !important; padding: 2.5pt 5pt !important; vertical-align: top; }',
        // Código
        'code { background: #F5F6FA !important; color: #1a1a1a !important; border: 0.5pt solid #E2E8F0;',
        '  padding: 0.5pt 3pt; font-size: 0.9em; border-radius: 2pt; }',
        'pre { background: #F5F6FA !important; color: #1a1a1a !important; padding: 6pt 8pt;',
        '  border: 0.5pt solid #E2E8F0; border-radius: 3pt; font-size: 8.5pt; line-height: 1.4; }',
        // Admonitions
        '.admonition, details { border: 0.5pt solid #E2E8F0 !important;',
        '  border-left: 2.5pt solid #81B1FA !important; background: #F5F6FA !important;',
        '  margin: 8pt 0; padding: 6pt 10pt; border-radius: 0; }',
        '.admonition-title, summary { color: #1D1F50 !important; font-weight: 600 !important;',
        '  background: transparent !important; padding-left: 0 !important; margin: 0 0 3pt 0 !important; }',
        '.admonition.warning, .admonition.danger { border-left-color: #E38000 !important; background: #FEF4E6 !important; }',
        '.admonition.warning .admonition-title, .admonition.danger .admonition-title { color: #8a4a00 !important; }',
        '.admonition.tip, .admonition.success { border-left-color: #17D1C6 !important; background: #E5FAF8 !important; }',
        '.admonition.tip .admonition-title, .admonition.success .admonition-title { color: #0A6E69 !important; }',
        '.admonition.quote { border-left-color: #6A7D94 !important; background: #F5F6FA !important; font-style: italic; }',
        // Tabs aplanados
        '.tabbed-set .tabbed-labels { display: none !important; }',
        '.tabbed-set input[type="radio"] { display: none !important; }',
        '.tabbed-content { display: block !important; }',
        '.tabbed-content .tabbed-block { display: block !important; padding: 0 !important; margin: 6pt 0 !important; }',
        // Imágenes y mermaid
        'img:not(.print-cover__logo) { max-width: 100% !important; height: auto !important; }',
        '.mermaid { margin: 12pt auto; text-align: center; }',
        '.mermaid svg { max-width: 100% !important; height: auto !important; display: block; margin: 0 auto; }',
        // Quote y separador
        'blockquote { border-left: 2.5pt solid #81B1FA; color: #2a3138; font-style: italic;',
        '  background: #F5F6FA; padding: 5pt 10pt; }',
        'hr { border: none; border-top: 0.5pt solid #E2E8F0; margin: 12pt 0; }',
      ].join('\n');
      styleEl.appendChild(clonedDoc.createTextNode(css));
      clonedDoc.head.appendChild(styleEl);

      // 2) Sustituir .print-cover__row (flex) por tabla 2 columnas
      clonedDoc.querySelectorAll<HTMLElement>('.print-cover__row').forEach((row) => {
        const logo = row.querySelector<HTMLElement>('.print-cover__logo');
        const doc = row.querySelector<HTMLElement>('.print-cover__doc');
        if (!logo || !doc) return;

        const table = clonedDoc.createElement('table');
        table.className = 'aunoa-cover-layout';
        table.style.cssText =
          'width:100%;border-collapse:collapse;border:none;margin:0 0 6pt 0;padding:0 0 6pt 0;border-bottom:1.2pt solid #1D1F50;table-layout:auto;';
        const tbody = clonedDoc.createElement('tbody');
        const tr = clonedDoc.createElement('tr');

        const tdLogo = clonedDoc.createElement('td');
        tdLogo.style.cssText =
          'width:1%;vertical-align:middle;padding:0;border:none !important;white-space:nowrap;background:transparent !important;';
        const logoClone = logo.cloneNode(true) as HTMLImageElement;
        logoClone.style.cssText =
          'height:22pt;max-height:22pt;max-width:110pt;width:auto;min-width:0;object-fit:contain;display:block;margin:0;';
        tdLogo.appendChild(logoClone);

        const tdDoc = clonedDoc.createElement('td');
        tdDoc.style.cssText =
          'text-align:right;vertical-align:middle;padding:0 0 0 12mm;border:none !important;background:transparent !important;';
        tdDoc.appendChild(doc.cloneNode(true));

        tr.appendChild(tdLogo);
        tr.appendChild(tdDoc);
        tbody.appendChild(tr);
        table.appendChild(tbody);
        row.parentNode?.replaceChild(table, row);
      });

      // 3) Mermaid: dejar solo el SVG, descartar texto fuente residual
      clonedDoc.querySelectorAll<HTMLElement>('.mermaid').forEach((el) => {
        const svg = el.querySelector('svg');
        if (svg) {
          const cleanSvg = svg.cloneNode(true);
          // Vaciar el contenedor con removeChild (más seguro que innerHTML='')
          while (el.firstChild) el.removeChild(el.firstChild);
          el.appendChild(cleanSvg);
        } else {
          // Si mermaid no se renderizó, ocultar (mejor que mostrar el código raw)
          el.style.display = 'none';
        }
      });

      // 4) Aplanar tabs: convertir cada label en heading antes de su block
      clonedDoc.querySelectorAll<HTMLElement>('.tabbed-set').forEach((tabSet) => {
        const labels = Array.from(
          tabSet.querySelectorAll<HTMLElement>('.tabbed-labels label'),
        );
        const blocks = Array.from(
          tabSet.querySelectorAll<HTMLElement>('.tabbed-content > .tabbed-block'),
        );
        blocks.forEach((block, i) => {
          const label = labels[i];
          if (!label) return;
          const heading = clonedDoc.createElement('h3');
          heading.textContent = label.textContent?.trim() || `Sección ${i + 1}`;
          heading.style.cssText =
            'color:#1D1F50;font-weight:600;font-size:11pt;margin:12pt 0 4pt;border-left:2.5pt solid #81B1FA;padding-left:7pt;';
          block.parentNode?.insertBefore(heading, block);
        });
      });

      // 5) Abrir todos los <details>
      clonedDoc.querySelectorAll<HTMLDetailsElement>('details').forEach((d) => {
        d.open = true;
      });
    };

    const opts = {
      margin: [10, 10, 12, 10],
      filename: computeFilename(shadowRoot, 'pdf'),
      image: { type: 'jpeg', quality: 0.96 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 1024,
        backgroundColor: '#ffffff',
        onclone,
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    await html2pdf().set(opts).from(article).save();
  } finally {
    setVisibility(cover, false);
    setVisibility(footer, false);
  }
}

async function exportDocx(shadowRoot: ShadowRoot): Promise<void> {
  const article = findArticle(shadowRoot);
  if (!article) throw new Error('No se encuentra el contenido del documento.');

  // html-docx-js convierte HTML → DOCX. La librería ofrece soporte limitado de
  // CSS (texto, listas, tablas, headings); no preserva mermaid ni layouts complejos
  // como el PDF. Para edición rápida en Word es suficiente.
  const htmlDocx = await import('html-docx-js/dist/html-docx');

  // Clonar el contenido y limpiar elementos UI que no aportan
  const clone = article.cloneNode(true) as HTMLElement;
  const removeSelectors = [
    '.md-content__button',
    'label#toggle-sidebar',
    '.md-skip',
    '.headerlink',
    'script',
  ];
  removeSelectors.forEach((sel) => {
    clone.querySelectorAll(sel).forEach((el) => el.remove());
  });

  // Hacer visible la cabecera dentro del clon (estaba display:none inline)
  const cover = clone.querySelector<HTMLElement>('.print-cover');
  if (cover) cover.style.display = 'block';
  const footer = clone.querySelector<HTMLElement>('.print-footer');
  if (footer) footer.style.display = 'block';

  // Ajustar logo: html-docx-js maneja imágenes data: y URLs absolutas
  const logo = clone.querySelector<HTMLImageElement>('.print-cover__logo');
  if (logo) {
    logo.style.cssText = 'height:30px;width:auto;';
  }

  // CSS básico para Word: tipografía + tablas con cabecera + márgenes
  const css = `
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #1a1a1a; }
    h1 { color: #1D1F50; font-size: 18pt; margin: 0 0 12pt; border-bottom: 1pt solid #81B1FA; padding-bottom: 4pt; }
    h2 { color: #1D1F50; font-size: 14pt; margin: 14pt 0 6pt; }
    h3 { color: #2D3082; font-size: 12pt; margin: 10pt 0 4pt; }
    h4 { color: #2D3082; font-size: 11pt; margin: 8pt 0 3pt; }
    table { border-collapse: collapse; width: 100%; margin: 8pt 0; font-size: 9pt; }
    table th { background: #1D1F50; color: #ffffff; border: 1px solid #1D1F50; padding: 4pt 6pt; text-align: left; }
    table td { border: 1px solid #E2E8F0; padding: 3pt 6pt; vertical-align: top; }
    code { background: #F5F6FA; padding: 1pt 3pt; font-family: Consolas, monospace; font-size: 0.9em; }
    pre { background: #F5F6FA; padding: 6pt; border: 1px solid #E2E8F0; font-family: Consolas, monospace; font-size: 9pt; }
    blockquote { border-left: 2pt solid #81B1FA; padding-left: 8pt; color: #2a3138; font-style: italic; }
    .admonition { border-left: 2pt solid #81B1FA; background: #F5F6FA; padding: 6pt 10pt; margin: 8pt 0; }
    .admonition-title { color: #1D1F50; font-weight: 600; }
    .print-cover { border-bottom: 1.2pt solid #1D1F50; padding-bottom: 8pt; margin-bottom: 14pt; }
    .print-cover__crumbs { font-size: 8pt; color: #6A7D94; text-transform: uppercase; letter-spacing: 0.05em; }
    .print-cover__title { font-size: 16pt; color: #1D1F50; font-weight: 700; margin: 4pt 0; }
    .print-cover__props { font-size: 9pt; background: #F5F6FA; padding: 4pt; }
    .print-cover__props th { background: transparent; color: #1D1F50; border: none; padding: 2pt 8pt 2pt 0; }
    .print-cover__props td { border: none; padding: 2pt 0; }
    .print-footer { border-top: 0.5pt solid #E2E8F0; padding-top: 6pt; margin-top: 24pt; font-size: 8pt; color: #6A7D94; }
    .print-footer__left { font-weight: 600; color: #1D1F50; }
  `;

  const fullHtml = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"><style>${css}</style></head>
<body>${clone.innerHTML}</body></html>`;

  const blob = htmlDocx.asBlob(fullHtml);
  downloadBlob(blob, computeFilename(shadowRoot, 'docx'));
}

function exportMarkdown(shadowRoot: ShadowRoot): void {
  const raw = readEmbedded(shadowRoot, 'aunoa-source-markdown') as string | null;
  if (!raw) {
    throw new Error(
      'El markdown original no está disponible para esta página. ' +
        'Verifica que el hook embed-source-md.py esté activo en mkdocs.yml.',
    );
  }
  const blob = new Blob([raw], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, computeFilename(shadowRoot, 'md'));
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export const TechDocsDownloadPdf = () => {
  const shadowRoot = useShadowRoot();
  const classes = useStyles();
  const [busy, setBusy] = useState<null | 'pdf' | 'docx' | 'md'>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const open = Boolean(anchor);
  const hasMarkdownSource = Boolean(
    shadowRoot && shadowRoot.getElementById('aunoa-source-markdown'),
  );

  const handleOpen = () => {
    if (buttonRef.current) setAnchor(buttonRef.current);
  };
  const handleClose = () => setAnchor(null);

  const run = async (
    kind: 'pdf' | 'docx' | 'md',
    fn: (root: ShadowRoot) => void | Promise<void>,
  ) => {
    handleClose();
    if (!shadowRoot || busy) return;
    setBusy(kind);
    try {
      await fn(shadowRoot);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[TechDocsDownloadPdf]', kind, err);
      // eslint-disable-next-line no-alert
      window.alert(`Error exportando como ${kind.toUpperCase()}:\n${err?.message ?? err}`);
    } finally {
      setBusy(null);
    }
  };

  const busyLabel = busy
    ? { pdf: 'Generando PDF…', docx: 'Generando DOCX…', md: 'Descargando MD…' }[busy]
    : null;

  return (
    <>
      <Tooltip title="Exportar este documento" placement="bottom">
        <span>
          <Button
            ref={buttonRef}
            variant="contained"
            size="small"
            className={classes.button}
            startIcon={
              busy ? (
                <CircularProgress size={14} style={{ color: '#fff' }} />
              ) : (
                <GetAppIcon />
              )
            }
            endIcon={!busy && <ArrowDropDownIcon />}
            onClick={handleOpen}
            disabled={Boolean(busy) || !shadowRoot}
            aria-label="Exportar"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            {busyLabel ?? 'Export'}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem className={classes.menuItem} onClick={() => run('pdf', exportPdf)}>
          <ListItemIcon className={classes.menuIcon}>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="PDF"
            secondary="Documento formateado, paleta corporativa"
            secondaryTypographyProps={{ style: { fontSize: '0.72rem' } }}
          />
        </MenuItem>

        <MenuItem className={classes.menuItem} onClick={() => run('docx', exportDocx)}>
          <ListItemIcon className={classes.menuIcon}>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="DOCX"
            secondary="Editable en Word"
            secondaryTypographyProps={{ style: { fontSize: '0.72rem' } }}
          />
        </MenuItem>

        <MenuItem
          className={classes.menuItem}
          onClick={() => run('md', exportMarkdown)}
          disabled={!hasMarkdownSource}
        >
          <ListItemIcon className={classes.menuIcon}>
            <CodeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Markdown original"
            secondary={
              hasMarkdownSource ? 'Fuente .md tal cual está en el repo' : 'No disponible para esta página'
            }
            secondaryTypographyProps={{ style: { fontSize: '0.72rem' } }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

attachTechDocsAddonComponentData(TechDocsDownloadPdf, {
  name: 'TechDocsDownloadPdf',
  location: TechDocsAddonLocations.Subheader,
  component: TechDocsDownloadPdf,
});
